import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, Copy, Gift, Loader2, ShieldAlert, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useBlueprintCredits } from "@/hooks/useBlueprintCredits";
import { referralLink } from "@/lib/referral";
import { ReferralTimeline } from "@/components/dashboard/ReferralTimeline";

type ReferralRow = {
  id: string;
  status: string;
  reward_credits: number;
  created_at: string;
  completed_at: string | null;
};

const REASON_LABELS: Record<string, string> = {
  purchase_starter_credits: "Purchase bonus",
  referral_reward_referrer: "Friend purchased",
  referral_reward_friend: "Welcome referral bonus",
  pdf_export: "PDF export",
  pdf_export_refund: "PDF export refunded",
};

/** Referral program: personal invite link, referral status, and credit ledger. */
export function ReferralPanel() {
  const { user } = useAuth();
  const { balance, lifetimeEarned, events, loading: creditsLoading, refetch } =
    useBlueprintCredits(user?.id);
  const [code, setCode] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<ReferralRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setBusy(true);
    const { data: rpcCode, error } = await supabase.rpc("get_or_create_referral_code");
    if (error) toast.error("Could not load your referral code");
    else setCode(rpcCode as string);
    const { data: rows } = await supabase
      .from("referrals")
      .select("id,status,reward_credits,created_at,completed_at")
      .eq("referrer_id", user.id)
      .order("created_at", { ascending: false });
    setReferrals((rows as ReferralRow[]) ?? []);
    setBusy(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async () => {
    if (!code) return;
    await navigator.clipboard.writeText(referralLink(code));
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const completed = referrals.filter((r) => r.status === "completed").length;
  const pending = referrals.filter((r) => r.status === "pending").length;
  // Referrals that tripped a fraud check wait on manual review rather than paying out.
  const heldForReview = referrals.filter((r) => r.status === "held_for_review").length;

  return (
    <section className="rounded-xl border border-border bg-card p-6" aria-labelledby="referral-heading">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 id="referral-heading" className="font-display text-xl font-bold flex items-center gap-2">
            <Gift className="size-5 text-primary" />
            Invite friends, earn blueprint downloads
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            When a friend buys a license with your link, you both get 3 extra blueprint PDF
            downloads. Rewards land automatically once the purchase is confirmed.
          </p>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl font-bold text-primary">
            {creditsLoading ? "—" : (balance ?? 0)}
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">download credits</div>
        </div>
      </div>

      <div className="mt-5 flex gap-2">
        <Input
          readOnly
          value={code ? referralLink(code) : busy ? "Loading…" : ""}
          aria-label="Your referral link"
        />
        <Button variant="outline" onClick={copy} disabled={!code} aria-label="Copy referral link">
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 mt-5">
        {[
          { label: "Completed referrals", value: completed, Icon: Users },
          { label: "Pending signups", value: pending, Icon: Loader2 },
          ...(heldForReview > 0
            ? [{ label: "Held for review", value: heldForReview, Icon: ShieldAlert }]
            : []),
          { label: "Credits earned all-time", value: lifetimeEarned, Icon: Gift },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border bg-secondary/30 p-3">
            <div className="font-mono text-[10px] uppercase text-muted-foreground">{s.label}</div>
            <div className="font-display text-2xl font-bold mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <h3 className="font-mono text-[10px] uppercase text-muted-foreground mb-2">
          referral timeline
        </h3>
        <ReferralTimeline />
      </div>

      {events.length > 0 && (
        <div className="mt-5">
          <h3 className="font-mono text-[10px] uppercase text-muted-foreground mb-2">credit history</h3>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {events.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="text-muted-foreground">
                  {REASON_LABELS[e.reason] ?? e.reason}
                  <span className="font-mono text-[10px] ml-2">
                    {new Date(e.created_at).toLocaleDateString()}
                  </span>
                </span>
                <span className={e.delta > 0 ? "text-primary font-semibold" : "text-muted-foreground"}>
                  {e.delta > 0 ? `+${e.delta}` : e.delta}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!creditsLoading && (balance ?? 0) === 0 && (
        <p className="mt-4 text-sm text-muted-foreground">
          No credits yet — share your link above, or{" "}
          <button onClick={refetch} className="underline hover:text-foreground">
            refresh
          </button>{" "}
          if you just completed a purchase.
        </p>
      )}
    </section>
  );
}
